import { useCallback, useEffect, useState } from 'react';
import { Check, Copy, KeyRound } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Skeleton } from '@/shared/ui/skeleton';
import { creerCle, dateCourte, getCles, revoquerCle } from '@/shared/api/bridge';
import { nomConfirme } from '@/features/bridge/bridge-etat';
import ErreurBloc from '@/features/bridge/components/ErreurBloc';
import CoupureTotale from '@/features/bridge/components/CoupureTotale';

/** Au-delà, une clé jamais utilisée est probablement restée dans un courriel. */
const JOURS_SANS_USAGE = 7;

const jamaisServie = (cle) => !cle.dernier_usage_le && cle.creee_le
  && Date.now() - new Date(cle.creee_le).getTime() > JOURS_SANS_USAGE * 86400000;

/**
 * Donner l'accès à un agrégateur, et le lui retirer.
 *
 * TROIS DÉFAUTS DE L'ANCIEN BLOC, ET CE QUI LES FERME
 *
 *   la clé naissait « clé survoltage », toutes pareilles  ->  un libellé est exigé :
 *        le jour où il faut en révoquer une sur trois, c'est lui qu'on lit
 *   la clé en clair n'avait pas de bouton copier          ->  on la copiait à la main,
 *        dans un bandeau qu'un clic ailleurs faisait perdre de vue
 *   « Révoquer » coupait l'accès en un clic                ->  on tape le nom de
 *        l'agrégateur : le geste est irréversible et coupe un partenaire en production
 *
 * La clé en clair n'est JAMAIS relisible : la base n'en garde que l'empreinte. Le
 * dialogue qui la montre ne se ferme qu'après la case « je l'ai transmise ».
 */
export default function OngletAcces({ agregateur, label, surChangement }) {
  const [cles, setCles] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [libelle, setLibelle] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [cleEnClair, setCleEnClair] = useState(null);
  const [transmise, setTransmise] = useState(false);
  const [copiee, setCopiee] = useState(false);
  const [aRevoquer, setARevoquer] = useState(null);
  const [saisie, setSaisie] = useState('');

  const charger = useCallback(async () => {
    setErreur(null);
    try {
      setCles(await getCles(agregateur));
    } catch (exc) {
      setErreur(exc);
    }
  }, [agregateur]);

  useEffect(() => { setCles(null); charger(); }, [charger]);

  const creer = async (e) => {
    e.preventDefault();
    if (!libelle.trim()) return;
    setEnCours(true);
    setErreur(null);
    try {
      const cle = await creerCle(agregateur, libelle.trim());
      setCleEnClair(cle);
      setTransmise(false);
      setCopiee(false);
      setLibelle('');
      await charger();
      surChangement?.();
    } catch (exc) {
      setErreur(exc);
    } finally {
      setEnCours(false);
    }
  };

  const copier = async () => {
    try {
      await navigator.clipboard.writeText(cleEnClair.cle);
      setCopiee(true);
    } catch {
      // Presse-papiers refusé (contexte non sécurisé) : la clé reste affichée et
      // sélectionnable, rien n'est perdu.
      setCopiee(false);
    }
  };

  const revoquer = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      await revoquerCle(agregateur, aRevoquer.id);
      setARevoquer(null);
      setSaisie('');
      await charger();
      surChangement?.();
    } catch (exc) {
      setErreur(exc);
      setARevoquer(null);
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="onglet-acces">
      <form onSubmit={creer} className="flex flex-wrap items-end gap-2">
        <label className="flex min-w-[260px] flex-1 flex-col gap-1 text-[12.5px] text-neutral-600">
          Libellé de la nouvelle clé
          <Input value={libelle} onChange={(e) => setLibelle(e.target.value)}
                 placeholder={`ex. production ${label}, septembre 2026`}
                 className="h-9 max-w-md" data-testid="bridge-cle-libelle" />
        </label>
        <Button type="submit" size="sm" disabled={!libelle.trim() || enCours}
                data-testid="bridge-cle-creer">
          <KeyRound className="mr-1.5 h-4 w-4" aria-hidden /> Créer une clé
        </Button>
      </form>

      <ErreurBloc erreur={erreur} testid="bridge-acces-erreur" />

      <CoupureTotale agregateur={agregateur} label={label}
                     actives={(cles || []).filter((c) => !c.revoquee_le).length}
                     surCoupure={async () => { await charger(); surChangement?.(); }} />

      {cles === null && !erreur && <Skeleton className="h-24 w-full" />}

      {cles?.length === 0 && (
        <p className="rounded-md border border-dashed border-neutral-300 px-4 py-6 text-center
                      text-[13px] text-neutral-500" data-testid="bridge-aucune-cle">
          Aucune clé. Sans clé, {label} ne peut rien nous demander.
        </p>
      )}

      {cles?.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-neutral-200">
          <table className="w-full min-w-[560px] text-[13px]">
            <thead className="bg-neutral-50 text-left text-[12px] text-neutral-500">
              <tr>
                <th className="px-3 py-2 font-normal">Libellé</th>
                <th className="font-normal">Début de clé</th>
                <th className="font-normal">Créée le</th>
                <th className="font-normal">Dernier usage</th>
                <th className="w-28" />
              </tr>
            </thead>
            <tbody>
              {cles.map((c) => (
                <tr key={c.id} className={`border-t border-neutral-100 ${c.revoquee_le ? 'text-neutral-400' : ''}`}
                    data-testid={`bridge-cle-${c.id}`}>
                  <td className="px-3 py-2">{c.libelle}</td>
                  <td><code className="text-[12px]">{c.prefixe}…</code></td>
                  <td className="whitespace-nowrap tabular-nums">{dateCourte(c.creee_le)}</td>
                  <td className={`whitespace-nowrap tabular-nums ${!c.revoquee_le && jamaisServie(c) ? 'text-amber-700' : ''}`}>
                    {c.dernier_usage_le ? dateCourte(c.dernier_usage_le) : 'jamais'}
                  </td>
                  <td className="pr-3 text-right">
                    {c.revoquee_le ? (
                      <span className="text-[12px]">révoquée le {dateCourte(c.revoquee_le)}</span>
                    ) : (
                      <Button size="sm" variant="outline"
                              onClick={() => { setARevoquer(c); setSaisie(''); }}
                              data-testid={`bridge-cle-revoquer-${c.id}`}>
                        Révoquer
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* --- La clé, une seule fois ------------------------------------------------- */}
      <Dialog open={Boolean(cleEnClair)}
              onOpenChange={(ouvert) => !ouvert && transmise && setCleEnClair(null)}>
        <DialogContent className="max-w-lg" data-testid="bridge-cle-en-clair">
          <DialogHeader>
            <DialogTitle className="text-base">Clé créée : elle ne sera plus jamais affichée</DialogTitle>
            <DialogDescription className="text-[13px]">
              Transmettez-la à {label} par un canal sûr. Si elle se perd, il faudra en créer
              une autre et révoquer celle-ci.
            </DialogDescription>
          </DialogHeader>
          {cleEnClair && (
            <div className="flex items-stretch gap-2">
              <code className="flex-1 select-all break-all rounded-md border border-neutral-200
                               bg-neutral-50 px-3 py-2 text-[12.5px]"
                    data-testid="bridge-cle-valeur">
                {cleEnClair.entete}: {cleEnClair.cle}
              </code>
              <Button type="button" size="sm" variant="outline" onClick={copier}
                      aria-label="Copier la clé" data-testid="bridge-cle-copier">
                {copiee ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          )}
          <label className="flex items-center gap-2 text-[13px] text-neutral-700">
            <Checkbox checked={transmise} onCheckedChange={(v) => setTransmise(Boolean(v))}
                      data-testid="bridge-cle-transmise" />
            J&apos;ai copié la clé et je la transmets à {label}
          </label>
          <DialogFooter>
            <Button size="sm" disabled={!transmise} onClick={() => setCleEnClair(null)}
                    data-testid="bridge-cle-fermer">
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- La révocation, confirmée par le nom ------------------------------------- */}
      <Dialog open={Boolean(aRevoquer)} onOpenChange={(ouvert) => !ouvert && !enCours && setARevoquer(null)}>
        <DialogContent className="max-w-md" data-testid="bridge-revocation">
          <DialogHeader>
            <DialogTitle className="text-base">Révoquer « {aRevoquer?.libelle} » ?</DialogTitle>
            <DialogDescription className="text-[13px] leading-relaxed">
              {label} ne pourra plus appeler le bridge avec cette clé, immédiatement.
              Les autres clés et les autres agrégateurs ne sont pas touchés. Ce geste ne
              s&apos;annule pas.
            </DialogDescription>
          </DialogHeader>
          <label className="flex flex-col gap-1 text-[12.5px] text-neutral-600">
            Tapez « {label} » pour confirmer
            <Input value={saisie} onChange={(e) => setSaisie(e.target.value)} autoFocus
                   data-testid="bridge-revocation-saisie" />
          </label>
          <DialogFooter>
            <Button size="sm" variant="outline" disabled={enCours}
                    onClick={() => setARevoquer(null)}>Annuler</Button>
            <Button size="sm" variant="destructive"
                    disabled={!nomConfirme(saisie, label) || enCours}
                    onClick={revoquer} data-testid="bridge-revocation-confirmer">
              Révoquer la clé
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
